import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  failureModeReviewSchema,
  failureModeScoreConfirmSchema,
} from '@/features/drbfm/schemas/failureModeReview.schema';
import { fieldErrorsOf } from '@/lib/forms/fieldErrors';
import { useInteracted } from '@/lib/forms/useInteracted';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import type {
  CountermeasureRequirement,
  FailureMode,
  UpdateFailureModeReviewRequest,
} from '../../../../types/drbfm';
import { CARD_INPUT, CARD_TEXTAREA, REJECT_BUTTON, SCORE_INPUT } from './styles';

export type ReviewFailureMode = (
  id: string,
  request: UpdateFailureModeReviewRequest,
) => void | Promise<void>;

interface FailureModeCardProps {
  mode: FailureMode;
  isLoading: boolean;
  onReview: ReviewFailureMode;
}

const FailureModeCard: React.FC<FailureModeCardProps> = ({ mode, isLoading, onReview }) => {
  const { t } = useTranslation();
  const { isTouched, onEdit } = useInteracted();
  const requiredForSod = t('drbfm.workflow.cards.requiredForSod', 'S/O/D確定に必須');
  const [description, setDescription] = React.useState(mode.failureDescription);
  const [cause, setCause] = React.useState(mode.failureCause || '');
  const [effect, setEffect] = React.useState(mode.failureEffect || '');
  const [severityScore, setSeverityScore] = React.useState(mode.severityScore);
  const [occurrenceScore, setOccurrenceScore] = React.useState<number | ''>(
    mode.occurrenceScore || '',
  );
  const [detectionScore, setDetectionScore] = React.useState<number | ''>(
    mode.detectionScore || '',
  );
  const [requirement, setRequirement] = React.useState<CountermeasureRequirement>(
    mode.countermeasureRequirement === 'pending' ? 'required' : mode.countermeasureRequirement,
  );
  const [notRequiredReason, setNotRequiredReason] = React.useState(
    mode.countermeasureNotRequiredReason || '',
  );

  const reviewValues = {
    failureDescription: description,
    failureCause: cause,
    failureEffect: effect,
    severityScore,
    countermeasureRequirement: requirement,
    countermeasureNotRequiredReason: notRequiredReason,
  };

  const baseRequest = (): UpdateFailureModeReviewRequest => ({
    reviewStatus: 'modified',
    ...reviewValues,
    // The request omits an empty reason; the schema measures the raw string.
    countermeasureNotRequiredReason: notRequiredReason || undefined,
  });
  const parsed = failureModeReviewSchema.safeParse(reviewValues);
  const contentIncomplete = !parsed.success;
  const errors = fieldErrorsOf(parsed);
  // Confirming S/O/D needs O and D as well, so the same object is parsed against the
  // confirm variant, which requires them. The '' sentinel is normalised first (as
  // CountermeasureCard does): fed raw, zod reports "expected number, received string"
  // on every AI candidate whose O/D are NULL, and the empty box is "not entered", not
  // "invalid". Its issues are what name the blocker when the button greys out.
  const scoresParsed = failureModeScoreConfirmSchema.safeParse({
    ...reviewValues,
    occurrenceScore: occurrenceScore === '' ? undefined : occurrenceScore,
    detectionScore: detectionScore === '' ? undefined : detectionScore,
  });
  const scoreErrors = fieldErrorsOf(scoresParsed);
  const scoresIncomplete = !scoresParsed.success;

  const confirmScore = () => {
    // The parsed data carries the validated numbers; the raw state still types as
    // number | '' because an empty box is a legal intermediate value.
    if (!scoresParsed.success) return;
    onReview(mode.id, {
      ...baseRequest(),
      occurrenceScore: scoresParsed.data.occurrenceScore,
      detectionScore: scoresParsed.data.detectionScore,
      scoreStatus: 'reviewed',
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="revy-cjk">
          {mode.reviewStatus === 'ai_candidate'
            ? t('drbfm.workflow.cards.aiCandidate', 'AI候補')
            : mode.reviewStatus}
        </Badge>
        <Badge variant="outline" className="revy-cjk revy-figure">
          {t('drbfm.workflow.cards.aiSeverityCandidate', 'AI重大度候補 {{score}}', {
            score: mode.severityCandidateScore ?? mode.severityScore,
          })}
        </Badge>
        <Badge variant="outline" className="revy-cjk">
          {t('drbfm.workflow.cards.scoringStatus', '採点: {{status}}', { status: mode.scoreStatus })}
        </Badge>
      </div>

      <Field label={t('drbfm.workflow.cards.failureMode', '故障モード')} required error={errors.failureDescription} errorLive={isTouched('failureDescription')}>
        {(control) => (
          <Input
            {...control}
            className={CARD_INPUT}
            value={description}
            onChange={onEdit('failureDescription', (event) => setDescription(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.cause', '原因')} required error={errors.failureCause} errorLive={isTouched('failureCause')}>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            className={CARD_TEXTAREA}
            value={cause}
            onChange={onEdit('failureCause', (event) => setCause(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.effect', '影響')} required error={errors.failureEffect} errorLive={isTouched('failureEffect')}>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            className={CARD_TEXTAREA}
            value={effect}
            onChange={onEdit('failureEffect', (event) => setEffect(event.target.value))}
          />
        )}
      </Field>

      {(mode.unknownAssumptions || []).length > 0 && (
        <p className="revy-cjk text-sm leading-[1.6] text-warning">
          {t('drbfm.workflow.cards.unconfirmed', '未確認: {{items}}', {
            items: (mode.unknownAssumptions || []).join(' / '),
          })}
        </p>
      )}

      <div className="flex flex-col gap-3 min-[900px]:flex-row">
        <Field label="S" className="min-[900px]:w-[200px]" required error={errors.severityScore} errorLive={isTouched('severityScore')}>
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              max={10}
              className={SCORE_INPUT}
              value={severityScore}
              onChange={onEdit('severityScore', (event) => setSeverityScore(Number(event.target.value)))}
            />
          )}
        </Field>
        <Field
          label="O"
          className="min-[900px]:w-[200px]"
          required
          requiredLabel={requiredForSod}
          error={scoreErrors.occurrenceScore}
          errorLive={isTouched('occurrenceScore')}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              max={10}
              className={SCORE_INPUT}
              value={occurrenceScore}
              onChange={onEdit('occurrenceScore', (event) => setOccurrenceScore(
                event.target.value === '' ? '' : Number(event.target.value),
              ))}
            />
          )}
        </Field>
        <Field
          label="D"
          className="min-[900px]:w-[200px]"
          required
          requiredLabel={requiredForSod}
          error={scoreErrors.detectionScore}
          errorLive={isTouched('detectionScore')}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              max={10}
              className={SCORE_INPUT}
              value={detectionScore}
              onChange={onEdit('detectionScore', (event) => setDetectionScore(
                event.target.value === '' ? '' : Number(event.target.value),
              ))}
            />
          )}
        </Field>
        <Field
          label={t('drbfm.workflow.cards.countermeasureRequirement', '対策要否')}
          className="min-[900px]:min-w-[180px]"
        >
          {(control) => (
            <Select
              value={requirement}
              onValueChange={onEdit(
                ['countermeasureRequirement', 'countermeasureNotRequiredReason'],
                (value) => setRequirement(value as CountermeasureRequirement),
              )}
            >
              <SelectTrigger {...control} className="revy-cjk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="revy-ui">
                <SelectItem value="required" className="revy-cjk">
                  {t('drbfm.workflow.cards.required', '必要')}
                </SelectItem>
                <SelectItem value="not_required" className="revy-cjk">
                  {t('drbfm.workflow.cards.notRequired', '不要')}
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>

      {requirement === 'not_required' && (
        <Field
          label={t('drbfm.workflow.cards.notRequiredReason', '対策不要の理由')}
          required
          error={errors.countermeasureNotRequiredReason}
          errorLive={isTouched('countermeasureNotRequiredReason')}
        >
          {(control) => (
            <Input
              {...control}
              required
              className={CARD_INPUT}
              value={notRequiredReason}
              onChange={onEdit('countermeasureNotRequiredReason', (event) => setNotRequiredReason(event.target.value))}
            />
          )}
        </Field>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={isLoading || contentIncomplete}
          onClick={() => onReview(mode.id, { ...baseRequest(), scoreStatus: 'provisional' })}
        >
          {t('drbfm.workflow.cards.adoptContent', '内容を採用')}
        </Button>
        <Button
          variant="outline"
          disabled={isLoading || scoresIncomplete}
          onClick={confirmScore}
        >
          {t('drbfm.workflow.cards.confirmSod', 'S/O/Dを確定')}
        </Button>
        <Button
          variant="outline"
          disabled={isLoading}
          onClick={() => onReview(mode.id, { reviewStatus: 'needs_info' })}
        >
          {t('drbfm.workflow.cards.needsInfo', '要確認')}
        </Button>
        <Button
          variant="outline"
          className={REJECT_BUTTON}
          disabled={isLoading}
          onClick={() => onReview(mode.id, { reviewStatus: 'rejected' })}
        >
          {t('drbfm.workflow.cards.reject', '却下')}
        </Button>
      </div>
    </div>
  );
};

export { FailureModeCard };
